// Widget „Fristen & Termine“ (Handoff §6.2): Fällig und Wiedervorlage als zwei Hälften (Klick = Datum
// inline, gespeichert mit „Fertig“ oder Enter über speichereFelder), darunter die verknüpften Termine als Kalenderblatt.
import { useState } from 'react';
import { Alarm, CalendarCheck, CalendarDots, Plus, X } from '@phosphor-icons/react';
import { WidgetHuelle } from '../../../../components/ui/WidgetHuelle';
import { faelligWidget } from '../../../../lib/tickets/darstellung';
import { istAktiv, terminZeitText, type TicketTermin } from '../../../../lib/tickets/terminLink';
import type { Ticket, TicketEditData } from '../types';
import { FertigKnopf, RundKlein, datumKurz } from './widgetTeile';

export interface WidgetFristenProps {
  ticket: Ticket;
  heute: string;
  termine: TicketTermin[];
  termineLaden: boolean;
  naechsterTerminId: string | null;
  onSpeichern: (teil: Partial<TicketEditData>) => void;
  onTerminNeu: () => void;
  onTerminOeffnen?: (id: string) => void;
  onTerminLoesen: (t: TicketTermin) => void;
}

type Haelfte = 'faellig' | 'wiedervorlage';

export function WidgetFristen({ ticket, heute, termine, termineLaden, naechsterTerminId, onSpeichern, onTerminNeu, onTerminOeffnen, onTerminLoesen }: WidgetFristenProps) {
  const [offen, setOffen] = useState<Haelfte | null>(null);
  // #597: Chrome ändert den Wert schon beim Blättern im Kalender (Monat vor/zurück) – daher erst mit „Fertig“/Enter speichern
  const [entwurf, setEntwurf] = useState('');
  const oeffne = (k: Haelfte) => { setEntwurf((k === 'faellig' ? ticket.faellig_am : ticket.wiedervorlage_am)?.slice(0, 10) || ''); setOffen(k); };
  const uebernehmen = (k: Haelfte) => {
    const alt = (k === 'faellig' ? ticket.faellig_am : ticket.wiedervorlage_am)?.slice(0, 10) || '';
    if (entwurf !== alt) onSpeichern(k === 'faellig' ? { faellig_am: entwurf } : { wiedervorlage_am: entwurf });
    setOffen(null);
  };
  const f = faelligWidget(ticket.faellig_am, ticket.status, heute);

  const haelfte = (k: Haelfte) => {
    const istF = k === 'faellig';
    const wert = istF ? f.wert : ticket.wiedervorlage_am ? datumKurz(ticket.wiedervorlage_am) : null;
    const unter = istF ? f.unter : ticket.wiedervorlage_notiz || null;
    const farbe = istF ? (f.ueberfaellig ? 'var(--bad)' : 'var(--warn)') : 'var(--info)';
    const I = istF ? CalendarCheck : Alarm;
    const auf = offen === k;
    return (
      <div role={auf ? undefined : 'button'} tabIndex={auf ? undefined : 0}
        onClick={auf ? undefined : () => oeffne(k)}
        onKeyDown={auf ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); oeffne(k); } }}
        data-tip={auf ? undefined : `${istF ? 'Fällig am – Bis wann das Ticket erledigt sein soll' : 'Wiedervorlage – An diesem Tag wieder vorlegen'}. Klicken zum Ändern`}
        className={`flex min-w-0 flex-1 flex-col ${auf ? '' : 'immo-zeile-hover cursor-pointer'}`}
        style={{ gap: 4, padding: '8px 10px', borderRadius: 14, boxShadow: auf ? 'inset 0 0 0 2px var(--acc)' : 'none' }}>
        <span className="inline-flex items-center" style={{ gap: 6, fontSize: 12.5, fontWeight: 700, color: farbe }}>
          <I size={15} weight="fill" /> {istF ? 'Fällig' : 'Wiedervorlage'}
        </span>
        {auf ? (
          <span className="flex min-w-0 flex-col" style={{ gap: 6 }}>
            <input type="date" value={entwurf} autoFocus aria-label={istF ? 'Fällig am' : 'Wiedervorlage am'}
              onChange={(e) => setEntwurf(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); uebernehmen(k); } else if (e.key === 'Escape') { e.preventDefault(); setOffen(null); } }}
              style={{ height: 36, width: '100%', minWidth: 0, padding: '0 8px', borderRadius: 10, border: 'none', outline: 'none', background: 'var(--bg)',
                color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700 }} />
            <FertigKnopf onClick={() => uebernehmen(k)} tip="Fertig – Datum übernehmen (Esc bricht ab)" />
          </span>
        ) : (
          <>
            <span className="truncate" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', color: wert ? (f.ueberfaellig && istF ? 'var(--bad)' : 'var(--ink)') : 'var(--ink3)' }}>
              {wert ?? (istF ? '+ Datum setzen' : '+ Wiedervorlage')}
            </span>
            {unter && <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: f.ueberfaellig && istF ? 'var(--bad)' : 'var(--ink3)' }}>{unter}</span>}
          </>
        )}
      </div>
    );
  };

  return (
    <WidgetHuelle titel="Fristen & Termine" icon={CalendarDots} farbe="var(--warn)" wasserzeichen={CalendarDots} style={{ flexShrink: 0 }}
      rechts={
        <button type="button" onClick={onTerminNeu} data-tip="Termin – Termin zu diesem Ticket anlegen (vorbelegt mit Titel und Objekt)"
          className="inline-flex items-center" style={{ height: 28, padding: '0 11px', gap: 5, borderRadius: 14, border: 'none', cursor: 'pointer',
            background: 'var(--acc-soft)', color: 'var(--acc)', fontFamily: 'inherit', fontSize: 12, fontWeight: 800 }}>
          <Plus size={11} weight="bold" /> Termin
        </button>
      }>
      <div className="flex items-stretch" style={{ gap: 6 }}>
        {haelfte('faellig')}
        <span aria-hidden style={{ width: 1, background: 'var(--line)', margin: '6px 0' }} />
        {haelfte('wiedervorlage')}
      </div>
      <div style={{ height: 1, background: 'var(--line)' }} />
      <div className="flex flex-col" style={{ gap: 6 }}>
        {termineLaden ? (
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Lädt …</span>
        ) : termine.length === 0 ? (
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Kein Termin verknüpft.</span>
        ) : termine.map((t) => {
          const naechster = naechsterTerminId === t.id;
          const abgesagt = !istAktiv(t);
          const d = new Date(t.start_datum);
          return (
            <div key={t.id} className="flex items-center" style={{ gap: 10, padding: 10, borderRadius: 16, opacity: abgesagt ? 0.6 : 1,
              background: naechster ? 'color-mix(in srgb, var(--acc) 12%, var(--fill))' : 'var(--fill)',
              boxShadow: naechster ? 'inset 0 0 0 1.5px var(--acc)' : 'none' }}>
              <button type="button" onClick={() => onTerminOeffnen?.(t.id)} data-tip={`${t.titel} – Im Kalender öffnen`}
                className="flex min-w-0 flex-1 items-center text-left" style={{ gap: 10, border: 'none', padding: 0, background: 'transparent', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
                <span aria-hidden className="flex shrink-0 flex-col items-center justify-center" style={{ width: 44, height: 48, borderRadius: 12, background: 'var(--bg)' }}>
                  <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--bad)', textTransform: 'uppercase' }}>
                    {Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '')}
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>{Number.isNaN(d.getTime()) ? '–' : d.getDate()}</span>
                </span>
                <span className="flex min-w-0 flex-col" style={{ gap: 1 }}>
                  {naechster && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--acc)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nächster Termin</span>}
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 800, textDecoration: abgesagt ? 'line-through' : 'none' }}>{t.titel}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{terminZeitText(t)}</span>
                </span>
              </button>
              <RundKlein icon={X} groesse={26} label="Verknüpfung entfernen" tip="Verknüpfung entfernen (Termin bleibt im Kalender)" onClick={() => onTerminLoesen(t)} />
            </div>
          );
        })}
      </div>
    </WidgetHuelle>
  );
}
